import * as React from 'react';
import { useMemo } from 'react';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import useScrollTrigger from '@mui/material/useScrollTrigger';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import Logo from '../assets/Logo/Company_logo.svg';
import WhiteLogo from '../assets/Logo/company_logo_white.svg';
import { useLocation, useNavigate } from 'react-router-dom';
import { scrollToSection } from '../utils/utils';

export default function Navbar(props) {
    const { t } = useTranslation();
    const theme = useTheme();
    const navItems = useMemo(() => [
        { key: 'home', path: '/', section: 'home' },
        { key: 'portfolio', path: '/portfolio', section: 'portfolio' },
        { key: 'services', path: '/services', section: 'services' },
        { key: 'about', path: '/about_us', section: 'about' },
        { key: 'contact', path: '/contact', section: 'contact' },
    ], []);
    // Separate page-route links (not scroll-to-section)
    const routeItems = useMemo(() => [{ key: 'engineers', path: '/engineers' }], []);

    const [mobileOpen, setMobileOpen] = React.useState(false);
    const [activeTab, setActiveTab] = React.useState('home');

    // listen to route changes so we can highlight the correct tab
    const location = useLocation();
    const navigate = useNavigate();

    // All paths that render the Home component (section deep-link routes)
    const homeSectionPaths = useMemo(
        () => new Set(['/', '/portfolio', '/services', '/about_us', '/contact']),
        []
    );

    // Navigate to a section: scroll directly if already on a home page, else do a full route navigate
    const handleSectionNav = React.useCallback((path, section) => {
        if (homeSectionPaths.has(location.pathname)) {
            // Already on Home — scroll in-place and silently update the URL
            // Using window.history.pushState instead of navigate() prevents React Router
            // from re-matching the route and remounting the Home component
            scrollToSection(section, setActiveTab);
            window.history.pushState({}, '', path);
        } else {
            // Coming from a different page (e.g. /developers) — normal navigate with scroll state
            navigate(path, { state: { scrollTo: section } });
        }
    }, [homeSectionPaths, location.pathname, navigate]);

    React.useEffect(() => {
        if (location.state?.scrollTo) {
            // Small delay ensures DOM is fully rendered
            setTimeout(() => {
                scrollToSection(location.state.scrollTo, setActiveTab);
            }, 100);
        }
    }, [location]);


    React.useEffect(() => {
        const observerOptions = {
            root: null,
            rootMargin: '-10% 0px -70% 0px', // More focused area to avoid multiple sections intersecting
            threshold: 0,
        };

        const observerCallback = (entries) => {
            if (!homeSectionPaths.has(location.pathname)) return; // Only update active tab via observer on home pages
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    setActiveTab(entry.target.id);
                }
            });
        };

        const observer = new IntersectionObserver(observerCallback, observerOptions);

        navItems.forEach((item) => {
            const element = document.getElementById(item.section);
            if (element) observer.observe(element);
        });

        // Add scroll listener for the very top of the page
        const handleScroll = () => {
            if (window.scrollY < 10 && homeSectionPaths.has(location.pathname)) {
                setActiveTab('home');
            }
        };
        window.addEventListener('scroll', handleScroll, { passive: true });

        return () => {
            observer.disconnect();
            window.removeEventListener('scroll', handleScroll);
        };
    }, [navItems, location.pathname, homeSectionPaths]);

    React.useEffect(() => {
        if (location.pathname === "/") {
            setActiveTab("home");
        } else if (location.pathname === "/portfolio") {
            setActiveTab("portfolio");
        } else if (location.pathname === "/services") {
            setActiveTab("services");
        } else if (location.pathname === "/about_us") {
            setActiveTab("about");
        } else if (location.pathname === "/contact") {
            setActiveTab("contact");
        } else if (location.pathname.startsWith("/project")) {
            setActiveTab("portfolio");
        } else if (location.pathname === "/engineers" || location.pathname.startsWith("/engineer/")) {
            setActiveTab("engineers");
        } else {
            setActiveTab("");
        }
    }, [location.pathname]);

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    // Move scroll trigger here to access state for styling
    const trigger = useScrollTrigger({
        disableHysteresis: true,
        threshold: 0,
        target: typeof window !== 'undefined' ? window : undefined,
    });

    const blueSections = new Set(['home']); // add any other dark/blue section keys
    const useWhiteLogo = !trigger && blueSections.has(activeTab);

    const appBarStyles = {
        backgroundColor: trigger ? theme.palette.custom.navbarBgScrolled : 'transparent',
        boxShadow: trigger ? 4 : 'none',
        transition: 'all 0.3s ease',
        paddingY: trigger ? 0.6 : 1,
        backdropFilter: trigger ? 'blur(10px)' : 'none',
    };

    // when the app bar is transparent (at top of page) the hero
    // background is very light, so white buttons disappear. use the
    // primary text color by default and only switch to the custom
    // scrolled colour once the user has moved.
    // const textColor = trigger
    //     ? theme.palette.custom.navbarTextScrolled || theme.palette.text.primary
    //     : theme.palette.text.primary;

    // const buttonHoverColor = trigger
    //     ? theme.palette.text.primary
    //     : 'rgba(255, 255, 255, 0.85)';

    const drawerContent = (
        <Box sx={{
            height: '100%',
            background: theme.palette.custom.mobileMenuBg,
            p: 3,
            display: 'flex',
            flexDirection: 'column'
        }}>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 4,
                    cursor: 'pointer'
                }}
                onClick={() => {
                    scrollToSection('home', setActiveTab);
                    handleDrawerToggle();
                }}
            >
                <img src={Logo} alt="NexusLink Logo" style={{ height: '40px' }} />
                <IconButton onClick={handleDrawerToggle} sx={{ color: theme.palette.text.primary }}>
                    <CloseIcon />
                </IconButton>
            </Box>

            <List sx={{ flexGrow: 1 }}>
                {[
                    ...navItems.map(({ key, path, section }) => ({ key, path, section, isSection: true })),
                    ...routeItems.map(r => ({ key: r.key, path: r.path, section: null, isSection: false })),
                ].map(({ key, path, section, isSection }) => {
                    const isActive = activeTab === key;

                    return (
                        <ListItem key={key} disablePadding sx={{ mb: 1 }}>
                            <Button
                                fullWidth
                                onClick={() => {
                                    if (isSection) {
                                        handleSectionNav(path, section);
                                    } else {
                                        navigate(path);
                                    }
                                    handleDrawerToggle();
                                }}
                                sx={{
                                    justifyContent: 'flex-start',
                                    textTransform: 'none',
                                    fontSize: '1.1rem',
                                    fontWeight: isActive ? 600 : 400,
                                    color: isActive
                                        ? theme.palette.primary.main
                                        : theme.palette.text.primary,
                                    px: 2,
                                    py: 1.2,
                                    borderRadius: '8px',
                                    position: 'relative',

                                    '&::before': {
                                        content: '""',
                                        position: 'absolute',
                                        left: 0,
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        height: '60%',
                                        width: '4px',
                                        borderRadius: '4px',
                                        backgroundColor: theme.palette.primary.main,
                                        opacity: isActive ? 1 : 0,
                                        transition: 'opacity 0.2s ease',
                                    },

                                    '&:hover': {
                                        backgroundColor: 'rgba(255,255,255,0.25)',
                                    },
                                }}
                            >
                                {t(`layout.navbar.${key}`)}
                            </Button>
                        </ListItem>
                    );
                })}
            </List>

            <Button
                variant="contained"
                fullWidth
                // onClick={() => {
                //     window.open('https://nexuslinkservices.com/contact-us', '_blank');
                // }}
                onClick={() => {
                    handleSectionNav('/contact', 'contact');
                    handleDrawerToggle();
                }}
                sx={{
                    backgroundImage: theme.palette.custom.buttonGradient,
                    color: theme.palette.common.white,
                    fontWeight: '500',
                    textTransform: 'none',
                    borderRadius: '8px',
                    py: 1.5,
                    boxShadow: 'none',
                    typography: 'textSm',
                }}
            >
                {t('layout.navbar.get_quote')}
            </Button>
        </Box>
    );

    return (
        <>
            <AppBar position="fixed" sx={appBarStyles} elevation={trigger ? 4 : 0}>
                <Container maxWidth="xl">
                    <Toolbar disableGutters>
                        {/* Logo */}
                        <Box
                            component="div"
                            onClick={() => scrollToSection('home', setActiveTab)}
                            sx={{
                                flexGrow: 1,
                                display: 'flex',
                                alignItems: 'center',
                                cursor: 'pointer'
                            }}
                        >
                            <img
                                src={useWhiteLogo ? WhiteLogo : Logo}
                                // src={trigger ? Logo : WhiteLogo}
                                alt="NexusLink Logo"
                                style={{ height: '50px', marginRight: '4px' }}
                            />
                            {/* <img src={Logo} alt="NexusLink Logo" style={{ height: '50px', marginRight: '4px' }} /> */}
                        </Box>

                        {/* Desktop Nav Items - sections + dedicated route pages */}
                        <Box sx={{ display: { xs: 'none', lg: 'block' } }}>
                            {[...navItems, ...routeItems.map(r => ({ key: r.key, path: r.path, section: null }))].map(({ key, path, section }) => {
                                const isActive = activeTab === key;

                                return (
                                    <Button
                                        key={key}
                                        onClick={() => section ? handleSectionNav(path, section) : navigate(path)}
                                        sx={{
                                            color: isActive ? theme.palette.primary.main : theme.palette.custom.navbarTextScrolled,
                                            mx: 1,
                                            textTransform: 'none',
                                            fontSize: '.875rem',
                                            fontWeight: 500,
                                            position: 'relative',
                                            lineHeight: '20px',
                                            letterSpacing: '0.015em',

                                            '&::after': {
                                                content: '""',
                                                position: 'absolute',
                                                bottom: 0,
                                                left: '50%',
                                                transform: 'translateX(-50%)',
                                                width: isActive ? '60%' : '0%',
                                                height: '2px',
                                                backgroundColor: theme.palette.primary.main,
                                                borderRadius: '2px',
                                                transition: 'width 0.3s ease',
                                            },

                                            '&:hover::after': {
                                                width: '60%',
                                            },

                                            '&:hover': {
                                                backgroundColor: 'transparent',
                                            },
                                        }}
                                    >
                                        {t(`layout.navbar.${key}`)}
                                    </Button>
                                );
                            })}

                        </Box>

                        {/* Desktop Get Quote Button */}
                        <Box sx={{ display: { xs: 'none', lg: 'block' } }}>
                            <Button
                                variant="contained"
                                // onClick={
                                //     () => window.open('https://nexuslinkservices.com/contact-us', '_blank')
                                // }
                                onClick={() => handleSectionNav('/contact', 'contact')}
                                sx={{
                                    ml: 2,
                                    backgroundImage: theme.palette.custom.buttonGradient,
                                    color: theme.palette.common.white,
                                    fontWeight: '500',
                                    textTransform: 'none',
                                    borderRadius: '8px',
                                    px: 2,
                                    py: 1,
                                    boxShadow: 'none',
                                    '&:hover': {
                                        backgroundImage: theme.palette.custom.buttonGradient,
                                        opacity: 0.9,
                                        boxShadow: 'none',
                                    },
                                    typography: 'textSm',
                                }}
                            >
                                {t('layout.navbar.get_quote')}
                            </Button>
                        </Box>

                        {/* Mobile Menu Button */}
                        <Box sx={{ display: { xs: 'block', lg: 'none' } }}>
                            <IconButton
                                color="inherit"
                                aria-label="open drawer"
                                edge="start"
                                onClick={handleDrawerToggle}
                                sx={{ color: theme.palette.custom.navbarTextScrolled }}
                            >
                                <MenuIcon />
                            </IconButton>
                        </Box>
                    </Toolbar>
                </Container>
            </AppBar>

            <Drawer
                anchor="top"
                open={mobileOpen}
                onClose={handleDrawerToggle}
                PaperProps={{
                    sx: {
                        height: 'auto',
                        maxHeight: '90vh',
                        borderRadius: '0 0 20px 20px',
                        overflow: 'hidden'
                    }
                }}
            >
                {drawerContent}
            </Drawer>
        </>
    );
}
